import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  IconAlarm,
  IconAlertTriangle,
  IconChevronRight,
  IconClipboardCheck,
  IconX,
} from '@tabler/icons-react';
import dayjs from 'dayjs';
import { Assignment, fetchControlAssignments } from '../../../entities/assignment';
import { fetchManagerGroupUserIds } from '../../../entities/group';
import type { UserRole } from '../../../entities/user/model/types';

const POLL_MS = 60_000;

const humanizeGap = (minutes: number): string => {
  const m = Math.max(0, Math.round(minutes));
  if (m < 60) return `${m} мин`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} ч`;
  return `${Math.floor(h / 24)} дн`;
};

const leadTitle = (a: Assignment) => a.expand?.tasksId?.title?.trim() || 'Без названия';

interface Props {
  userId: string;
  role: UserRole;
}

const TaskControl: React.FC<Props> = ({ userId, role }) => {
  const navigate = useNavigate();
  const [urgent, setUrgent] = useState<Assignment[]>([]);
  const [overdue, setOverdue] = useState<Assignment[]>([]);
  const [isOpen, setIsOpen] = useState(false);

  const load = useCallback(async () => {
    if (!userId) return;
    try {
      // MANAGER — по всем участникам своих групп; ADMIN/USER — только свои
      let ownerIds = [userId];
      if (role === 'MANAGER') {
        const groupIds = await fetchManagerGroupUserIds(userId);
        if (groupIds.length) ownerIds = groupIds;
      }
      const { urgent, overdue } = await fetchControlAssignments(ownerIds);
      setUrgent(urgent);
      setOverdue(overdue);
    } catch {
      // ignore
    }
  }, [userId, role]);

  useEffect(() => {
    load();
    const id = window.setInterval(load, POLL_MS);
    return () => window.clearInterval(id);
  }, [load]);

  useEffect(() => {
    if (!isOpen) return;
    load();
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setIsOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, load]);

  const goToCard = (a: Assignment) => {
    const leadId = a.expand?.tasksId?.id ?? a.tasksId;
    if (!leadId) return;
    setIsOpen(false);
    navigate(`/kanban?card=${leadId}`);
  };

  const renderItem = (a: Assignment, kind: 'urgent' | 'overdue') => {
    const mins = dayjs(a.planDate).diff(dayjs(), 'minute');
    const label = kind === 'urgent'
      ? `Осталось ${humanizeGap(mins)}`
      : `Просрочено на ${humanizeGap(-mins)}`;
    return (
      <button className="tctrl-item" type="button" key={a.id} onClick={() => goToCard(a)}>
        <div className="tctrl-item-body">
          <div className="tctrl-item-name">{a.name}</div>
          <div className="tctrl-item-lead">
            {leadTitle(a)}
            {role === 'MANAGER' && a.expand?.ownerId?.name && (
              <span className="tctrl-item-owner"> · {a.expand.ownerId.name}</span>
            )}
          </div>
        </div>
        <div className="tctrl-item-right">
          <span className={`tctrl-item-time tctrl-${kind}`}>
            {dayjs(a.planDate).format('DD.MM HH:mm')}
          </span>
          <span className="tctrl-item-gap">{label}</span>
        </div>
        <IconChevronRight size={16} stroke={2} className="tctrl-item-chev" />
      </button>
    );
  };

  const column = (kind: 'urgent' | 'overdue', items: Assignment[]) => {
    const isUrgent = kind === 'urgent';
    return (
      <div className={`tctrl-col tctrl-col-${kind}`}>
        <div className="tctrl-col-head">
          {isUrgent ? <IconAlarm size={16} stroke={2} /> : <IconAlertTriangle size={16} stroke={2} />}
          <span>{isUrgent ? 'Срочные' : 'Просроченные'}</span>
          <span className="tctrl-col-count">{items.length}</span>
        </div>
        <div className="tctrl-col-list">
          {items.length === 0
            ? <div className="tctrl-empty">{isUrgent ? 'Нет срочных задач' : 'Нет просроченных задач'}</div>
            : items.map(a => renderItem(a, kind))}
        </div>
      </div>
    );
  };

  return (
    <>
      <button
        className={`tctrl-btn${overdue.length > 0 ? ' has-overdue' : ''}`}
        type="button"
        onClick={() => setIsOpen(true)}
        title="Контроль задач"
      >
        <IconClipboardCheck size={16} stroke={2} />
        <span className="tctrl-btn-label">Контроль задач</span>
        <span className={`tctrl-badge tctrl-badge-urgent${urgent.length === 0 ? ' zero' : ''}`} title="Срочные">
          {urgent.length}
        </span>
        <span className={`tctrl-badge tctrl-badge-overdue${overdue.length === 0 ? ' zero' : ''}`} title="Просроченные">
          {overdue.length}
        </span>
      </button>

      {isOpen && createPortal(
        <div className="mo open" onClick={() => setIsOpen(false)}>
          <div className="cm tctrl-modal" onClick={e => e.stopPropagation()}>
            <div className="cm-top" style={{ borderRadius: '20px 20px 0 0' }}>
              <div>
                <div className="cm-ti">Контроль задач</div>
                <div className="cm-sub">Срочные — меньше часа до срока · Просроченные — срок истёк</div>
              </div>
              <div className="cm-sp" />
              <button className="cm-x" onClick={() => setIsOpen(false)} type="button">
                <IconX size={18} stroke={2.2} />
              </button>
            </div>
            <div className="tctrl-cols">
              {column('urgent', urgent)}
              {column('overdue', overdue)}
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
};

export { TaskControl };
